import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Kbd } from '@/components/ui/kbd'
import { NAV_SHORTCUT_LABELS, NAV_SHORTCUTS } from '@/hooks/useNavShortcuts'

interface KeyboardShortcutsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * The `?` help overlay. Lists the global navigation chords surfaced by
 * `useNavShortcuts`. Stays a static reference card — no live wiring needed.
 */
export function KeyboardShortcutsDialog({
  open,
  onOpenChange,
}: KeyboardShortcutsDialogProps) {
  const entries = Object.keys(NAV_SHORTCUTS) as (keyof typeof NAV_SHORTCUTS)[]
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="keyboard-shortcuts-dialog"
      >
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            Press <Kbd>G</Kbd> followed by a letter to jump between sections.
            Press <Kbd>?</Kbd> any time to reopen this card.
          </DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-border text-sm">
          {entries.map((k) => (
            <li
              key={k}
              className="flex items-center justify-between gap-3 py-2.5"
            >
              <span className="text-foreground">{NAV_SHORTCUT_LABELS[k]}</span>
              <span className="flex items-center gap-1 text-muted-foreground">
                <Kbd>G</Kbd>
                <span className="text-xs">then</span>
                <Kbd>{k.toUpperCase()}</Kbd>
              </span>
            </li>
          ))}
          <li className="flex items-center justify-between gap-3 py-2.5">
            <span className="text-foreground">Show this help</span>
            <Kbd>?</Kbd>
          </li>
        </ul>
      </DialogContent>
    </Dialog>
  )
}
